import React, { useEffect } from 'react';
import { Sparkles, X, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { RELEASES, TAG_COLORS } from '../../data/releaseNotes';

/**
 * HotfixBundleModal — "what's new in this release" popup.
 *
 * v6.8.46 rewrite (Feb 28, 2026):
 * • Pulls items from `/data/releaseNotes.js` (the SAME source as the
 *   in-app release-notes panel) instead of a hardcoded FIXES array.
 *   Means the modal automatically stays current with every release.
 * • Filters items by `audience` (`'all' | 'provider' | 'patient'`) so
 *   patients see patient-relevant updates and providers see
 *   provider-relevant updates. Items default to `'all'` when the tag
 *   is omitted.
 * • Per-release dismissal flag (`writeright_hotfix_seen_<VERSION>`)
 *   prevents re-prompting on this device.
 * • Per-audience contrast tuned — header gradient + Got-it button now
 *   honor `prefers-color-scheme` so white-on-orange unreadability
 *   reported on Feb 28, 2026 is gone.
 */

// Bump VERSION whenever a new release ships — derived from the latest entry
// in releaseNotes.js so we never forget.
const LATEST = RELEASES[0] || { version: '0.0.0', items: [] };
const VERSION = `v${LATEST.version}`;
const FLAG_KEY = `writeright_hotfix_seen_${VERSION}`;

export const isHotfixBundleSeen = () => {
  try { return localStorage.getItem(FLAG_KEY) === '1'; }
  catch { return true; }
};
// Back-compat alias — older callers import as `hasSeenHotfixBundle`.
export const hasSeenHotfixBundle = isHotfixBundleSeen;

export const markHotfixBundleSeen = () => {
  try { localStorage.setItem(FLAG_KEY, '1'); } catch { /* ignore */ }
};

/**
 * Compute the audience-filtered item list for the latest release.
 * Default audience = 'all' so legacy entries without the tag still
 * show for everyone (back-compat).
 */
const filterForAudience = (items, audience) =>
  (items || []).filter((it) => {
    const a = it.audience || 'all';
    return a === 'all' || a === audience;
  });

const HotfixBundleModal = ({ open, onClose, audience = 'all' }) => {
  // Mar 2, 2026 — Auto-dismiss the popup as soon as the provider starts
  // typing anywhere (notes textarea, chief-complaint, etc.). The bottom-
  // left placement was still bleeding into the notes column on narrow
  // desktops; dismissing on first keystroke means providers never fight
  // it. Hook must live BEFORE the early `return null` so React's rules
  // of hooks aren't violated (hook count must be stable per render).
  useEffect(() => {
    if (!open) return;
    const onFirstKeystroke = (e) => {
      const t = e.target;
      const isTextInput = t && (
        t.tagName === 'TEXTAREA' ||
        (t.tagName === 'INPUT' && !['button','checkbox','radio','submit','file','range'].includes((t.type || '').toLowerCase())) ||
        t.isContentEditable
      );
      if (!isTextInput) return;
      markHotfixBundleSeen();
      onClose?.();
    };
    window.addEventListener('input', onFirstKeystroke, { capture: true });
    return () => window.removeEventListener('input', onFirstKeystroke, { capture: true });
  }, [open, onClose]);

  if (!open) return null;

  const items = filterForAudience(LATEST.items, audience);
  if (items.length === 0) {
    // Nothing relevant for this audience — mark seen + auto-dismiss so
    // patients don't get an empty modal because every item in the release
    // happened to be provider-only.
    markHotfixBundleSeen();
    return null;
  }

  const handleDismiss = () => {
    markHotfixBundleSeen();
    onClose?.();
  };

  return (
    <div
      className="fixed bottom-4 left-4 z-40 w-[min(440px,calc(100vw-2rem))] max-h-[70vh] pointer-events-none animate-in slide-in-from-bottom-4 fade-in"
      data-testid="hotfix-bundle-backdrop"
      onKeyDown={(e) => { if (e.key === 'Escape') handleDismiss(); }}
    >
      <div
        className="bg-card text-foreground border border-amber-500/40 rounded-xl shadow-2xl flex flex-col max-h-[70vh] overflow-hidden pointer-events-auto"
        data-testid="hotfix-bundle-modal"
        role="dialog"
        aria-labelledby="hotfix-bundle-title"
      >
        {/* Header — contrast-safe gradient + foreground text */}
        <div className="flex items-start justify-between px-5 py-3 border-b border-border bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-amber-500/20 border border-amber-500/40 shrink-0">
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            <div className="min-w-0">
              <h2 id="hotfix-bundle-title" className="text-sm font-bold text-foreground leading-snug">
                What changed in this release
              </h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                WriteRight PCM+ {VERSION} · {LATEST.date || 'just shipped'} · {items.length} update{items.length === 1 ? '' : 's'} {audience !== 'all' ? `for ${audience}s` : ''}
              </p>
              {LATEST.codename && (
                <p className="text-[10px] italic text-muted-foreground/80 mt-0.5 truncate">
                  &ldquo;{LATEST.codename}&rdquo;
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="h-7 w-7 -mt-0.5 -mr-1 shrink-0"
            data-testid="hotfix-bundle-close"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2.5">
          {items.map((it, i) => {
            const tagColor = TAG_COLORS?.[it.tag] || '#f59e0b';
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-2.5 rounded-lg border border-border/60 bg-secondary/20"
                data-testid={`hotfix-bundle-item-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ color: tagColor, backgroundColor: `${tagColor}20`, border: `1px solid ${tagColor}40` }}
                    >
                      {it.tag || 'UPDATE'}
                    </span>
                    {it.audience && it.audience !== 'all' && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30">
                        for {it.audience}s
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground leading-snug">
                    {it.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {it.desc}
                  </p>
                </div>
              </div>
            );
          })}

          <div className="text-[11px] text-muted-foreground/80 leading-relaxed pt-2 border-t border-border">
            Installed as a PWA? Hard-refresh once (Ctrl+Shift+R / Cmd+Shift+R)
            to make sure you&apos;re on this build. This notice will not show
            again on this device.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-2.5 border-t border-border bg-secondary/30">
          <Button
            onClick={handleDismiss}
            className="h-8 text-xs font-semibold gap-2 bg-amber-500/90 hover:bg-amber-500 text-amber-950 px-4"
            data-testid="hotfix-bundle-dismiss"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HotfixBundleModal;
